import { SDK_SEASON, type FootballGradeFacet, type PositionGrade } from '@pff-consumer/schema'
import { isAnyOf } from '@reduxjs/toolkit'
import { connectSlice } from '../../lib/connect/connect.slice'
import { consumerApi } from '../../lib/consumer-api/consumer-api'
import type { AppRootState, AppStartListening } from '../store'

export const addConnectListeners = (startListening: AppStartListening) => {
  startListening({
    matcher: consumerApi.endpoints.getPFFLiveData.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      const events = action.payload?.receiving?.events || []
      const narratives = action.payload?.narratives
      const state = listenerApi.getState() as AppRootState
      const { playerPositionGradeMap } = state.connect
      const metricEvents = events.map((mEvent) => ({
        eventId: mEvent.eventId,
        quarter: mEvent.quarter,
        gameClock: mEvent.gameClock,
        homeScore: mEvent.homeScore,
        awayScore: mEvent.awayScore,
      }))
      listenerApi.dispatch(connectSlice.actions.setEventScores(metricEvents))

      if (narratives) {
        const passRateNarratives = narratives.passRate?.events || []
        const pressureRateNarratives = narratives.pressureRate?.events || []
        listenerApi.dispatch(connectSlice.actions.setPassRateNarratives(passRateNarratives))
        listenerApi.dispatch(connectSlice.actions.setPressureRateNarratives(pressureRateNarratives))
      }

      const { selectedEvent } = state.connect

      const receivingEventData = (action?.payload?.receiving?.events || []).find(
        (metricData) => metricData.eventId === selectedEvent
      )
      const rushingEventData = (action.payload?.rushing?.events || []).find(
        (metricData) => metricData.eventId === selectedEvent
      )

      listenerApi.dispatch(connectSlice.actions.processPFFLivePlayers({ receivingEventData, rushingEventData }))

      if (!playerPositionGradeMap && events) {
        const playerIds = events.reduce<number[]>((acc, { players }) => {
          return [...acc, ...players.map(({ playerId }) => playerId)]
        }, [])
        const { data, error } = await listenerApi.dispatch(
          consumerApi.endpoints.getPlayerGradesByIds.initiate({ ids: playerIds })
        )
        if (!data || error) return
        // Create a map of the playerID with their position grade like coverage grade & ranks for CB
        const playerRankingsMap =
          data.reduce<Record<number, Record<FootballGradeFacet, PositionGrade[]>>>((acc, { grades, playerId }) => {
            const gradesMapByFacet: Record<string, PositionGrade[]> = {}
            grades.forEach((grade) => {
              const gradesByFacet = gradesMapByFacet[grade.facet]
              const updatedGradesByFacet = gradesByFacet ? [...gradesByFacet, grade] : [grade]
              gradesMapByFacet[grade.facet] = updatedGradesByFacet
            })
            return { ...acc, [playerId]: gradesMapByFacet }
          }, {}) || null
        listenerApi.dispatch(connectSlice.actions.setPlayerPositionGradeMap(playerRankingsMap))
      }
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getPreGamePassNarratives.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      if (action.payload?.players?.length) {
        listenerApi.dispatch(connectSlice.actions.setPreGameNarratives(action.payload.players))
      }
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getSDKHalftimeReceivingMetrics.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      if (action.payload?.players?.length) {
        listenerApi.dispatch(connectSlice.actions.setPlayerHalftimeReceivingMetricsMap(action.payload.players))
      }
    },
  })

  startListening({
    matcher: consumerApi.endpoints.getSDKHalftimeRushingMetrics.matchFulfilled,
    effect: async (action, listenerApi) => {
      // Can cancel other running instances
      listenerApi.cancelActiveListeners()
      if (action.payload?.players?.length) {
        listenerApi.dispatch(connectSlice.actions.setPlayerHalftimeRushingMetricsMap(action.payload.players))
      }
    },
  })

  startListening({
    actionCreator: connectSlice.actions.setSelectedTeamAbv,
    effect: async (action, listenerApi) => {
      listenerApi.cancelActiveListeners()
      const state = listenerApi.getState() as AppRootState
      const { selectedWeek, selectedEvent } = state.connect
      const filters = { season: SDK_SEASON, weekAbv: selectedWeek! }
      const { data } = consumerApi.endpoints.getPFFLiveData.select(filters)(state)
      const receivingEventData = (data?.receiving?.events || []).find(
        (metricData) => metricData.eventId === selectedEvent
      )
      const rushingEventData = (data?.rushing?.events || []).find((metricData) => metricData.eventId === selectedEvent)
      listenerApi.dispatch(connectSlice.actions.processPFFLivePlayers({ receivingEventData, rushingEventData }))
    },
  })

  /**
   * The SelectedTeamAbv value needs to be cleared when the event ID or the Week is changed.
   * Otherwsie it could affect which players are being returns on the usage tab.
   */
  startListening({
    matcher: isAnyOf(connectSlice.actions.setSelectedEvent, connectSlice.actions.setSelectedWeek),
    effect: async (action, listenerApi) => {
      listenerApi.dispatch(connectSlice.actions.setSelectedTeamAbv(null))
    },
  })
}
